package fyi.moko.hotpl.domain.model

/**
 * What a picked photo can tell us about a visit, read from its EXIF before the bytes are
 * downscaled (the re-encode drops the tags — see `ExifOrientation` / `downscaleToJpeg`).
 * Either field can be missing; a screenshot has neither.
 */
data class PhotoEvidence(
    val capturedAt: Long? = null,
    val location: GeoPoint? = null,
)

/** Why [inferVisit] decided what it decided. Shown to the user as one short line. */
enum class EvidenceReason {
    /** A photo's own GPS puts the camera at the place. */
    PHOTO_LOCATION,

    /** The device is at the place right now. */
    HERE_NOW,

    /** The device is somewhere else, and knows it — this is how a saved-for-later place is born. */
    FAR_AWAY,

    /** No usable signal. The app does not guess. */
    UNKNOWN,
}

/**
 * The status a new place should start with, and when its first visit happened.
 *
 * [visitedAt]가 null이면 방문을 만들지 않는다 — 저장소는 이미
 * `status == VISITED`일 때만 첫 방문을 만들므로 둘은 항상 같이 움직인다.
 */
data class VisitEvidence(
    val status: PlaceStatus,
    val visitedAt: Long?,
    val reason: EvidenceReason,
)

/** 사진 GPS가 이만큼 안이면 그 자리에서 찍은 것으로 본다. 길 건너에서 찍는 경우까지 담는다. */
const val PHOTO_RADIUS_M = 150f

/** 저장하는 순간의 내 위치 기준. 도심 GPS 오차(20~50 m)에 가게 하나 폭을 더한 값이다. */
const val LIVE_RADIUS_M = 120f

/** 이보다 오래된 좌표는 "지금 여기"의 근거가 못 된다. 콜드 스타트의 last-known은 몇 시간 전일 수 있다. */
const val FIX_MAX_AGE_MS = 5 * 60 * 1000L

/** 이 안에 찍힌 사진이면 이번 나들이의 것으로 보고 방문 시각으로 쓴다. */
const val PHOTO_SAME_OUTING_MS = 24 * 60 * 60 * 1000L

/**
 * 새 장소를 저장할 때 **가본 곳인지 아닌지를 뒷단에서 정한다.**
 *
 * ── 사다리 (위에서부터 먼저 맞는 것 하나) ──────────────────────────────
 *  1. 사진 EXIF의 GPS가 장소에서 [PHOTO_RADIUS_M] 안 → 가봤음, 방문 시각은 **사진을 찍은 때**
 *  2. GPS 없이 촬영 시각만 있으면 상태는 못 정한다 — 아래로 내려가되 시각만 빌려 쓴다
 *  3. 지금 내 위치가 [LIVE_RADIUS_M] 안이고 좌표가 신선하면 → 가봤음, 지금
 *  4. 위치를 아는데 멀면 → **안 가봤음**, 방문 없음
 *  5. 아무것도 모르면 → 가봤음(기존 동작 유지)
 *
 * 사진을 맨 위에 두는 이유는 그것이 질문에 정확히 답하기 때문이다. 사진의 GPS는 **찍은
 * 순간 그 자리에서** 기록된 것이라, "저장하는 지금 내가 어디 있는가"보다 "내가 거기 있었는가"를
 * 직접 말한다. 덕분에 몇 달 전 사진을 오늘 넣는 경우까지 옳게 처리된다 — 가봤음이고, 방문은
 * 오늘이 아니라 그날이다.
 *
 * ── 모를 때는 추측하지 않는다 ──────────────────────────────
 * 5번이 기존 동작(`VISITED`)을 그대로 두는 것은 의도다. 위치 권한은 선택 사항이고, 신호가
 * 없다는 것은 "안 갔다"는 뜻이 아니다. 증거의 부재로 상태를 만들어 내면, 권한을 끈 사용자의
 * 모든 기록이 조용히 '가보고 싶은 곳'이 된다.
 */
fun inferVisit(
    place: GeoPoint?,
    photos: List<PhotoEvidence>,
    currentLocation: GeoPoint?,
    fixAgeMillis: Long?,
    now: Long,
): VisitEvidence {
    val photoTakenNearby = place?.let { target ->
        photos.filter { it.location != null }
            .minByOrNull { haversineMeters(it.location!!, target) }
            ?.takeIf { haversineMeters(it.location!!, target) <= PHOTO_RADIUS_M }
    }
    if (photoTakenNearby != null) {
        return VisitEvidence(
            status = PlaceStatus.VISITED,
            visitedAt = photoTakenNearby.capturedAt ?: now,
            reason = EvidenceReason.PHOTO_LOCATION,
        )
    }

    // 이번 나들이의 사진이 있으면 방문 시각은 그쪽이 맞다 — 저녁에 앉아서 적어도 낮에 간 것이다.
    val outingTime = photos.mapNotNull { it.capturedAt }
        .filter { it in (now - PHOTO_SAME_OUTING_MS)..now }
        .maxOrNull()

    val fixIsFresh = fixAgeMillis != null && fixAgeMillis <= FIX_MAX_AGE_MS
    if (place != null && currentLocation != null && fixIsFresh) {
        val metres = haversineMeters(currentLocation, place)
        return if (metres <= LIVE_RADIUS_M) {
            VisitEvidence(PlaceStatus.VISITED, outingTime ?: now, EvidenceReason.HERE_NOW)
        } else {
            VisitEvidence(PlaceStatus.WISHLIST, visitedAt = null, reason = EvidenceReason.FAR_AWAY)
        }
    }

    return VisitEvidence(PlaceStatus.VISITED, outingTime ?: now, EvidenceReason.UNKNOWN)
}
